import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useFileDrop, { isTouchDevice } from '../hooks/useFileDrop';

/** Keep only what the caller asked for: a drop can carry anything. */
function matchesAccept(file, accept) {
  if (!accept || accept === '*') return true;
  return accept.split(',').some((rule) => {
    rule = rule.trim().toLowerCase();
    if (rule.endsWith('/*')) return (file.type || '').startsWith(rule.slice(0, -1));
    if (rule.startsWith('.')) return (file.name || '').toLowerCase().endsWith(rule);
    return (file.type || '').toLowerCase() === rule;
  });
}

/**
 * The one place photos get picked, wherever that happens in the app.
 *
 * Three routes of equal standing: drop a file on it, browse for one, or — on a
 * phone — shoot one. `children` is where a caller adds its own (the wizard puts
 * the "from my phone" QR button there).
 *
 * Pass `dragging` + `dropHandlers` to have a larger container drive the drag
 * state instead: the wizard makes the whole step panel the target, so nobody
 * has to aim at the strip.
 */
export default function PhotoDropzone({
  onFiles,
  multiple = true,
  // .heic/.heif listed by name: on Windows they often have no MIME type, and
  // the server converts them to WEBP (backend services/image_convert).
  accept = 'image/*,.heic,.heif',
  label,
  hint,
  dragging: draggingProp,
  dropHandlers: dropHandlersProp,
  disabled = false,
  children,
}) {
  const { t } = useTranslation();
  const browseRef = useRef(null);
  const cameraRef = useRef(null);

  const emit = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => matchesAccept(f, accept));
    if (files.length) onFiles(files);
  };

  const [ownDragging, ownHandlers] = useFileDrop(emit);
  const dragging = draggingProp !== undefined ? draggingProp : ownDragging;
  const handlers = dropHandlersProp || ownHandlers;

  // Clearing the input is what lets the same file be picked twice in a row.
  const pick = (ref) => (e) => {
    emit(e.target.files);
    if (ref.current) ref.current.value = '';
  };

  return (
    <div
      className={`photo-dropzone ${dragging ? 'is-over' : ''} ${disabled ? 'is-disabled' : ''}`}
      {...(dropHandlersProp ? {} : handlers)}
    >
      <div className="photo-dropzone-main">
        <svg className="photo-dropzone-icon" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
          <path d="M3 16.5V19a2 2 0 002 2h14a2 2 0 002-2v-2.5" strokeLinecap="round" />
          <path d="M12 15V3m0 0L8 7m4-4l4 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div>
          <p className="photo-dropzone-label">{label || t('upload.dropHere')}</p>
          <p className="photo-dropzone-hint">{hint || t('upload.dropHint')}</p>
        </div>
      </div>

      <div className="photo-dropzone-actions">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={disabled}
          onClick={() => browseRef.current?.click()}
        >
          {t('upload.browse')}
        </button>
        {isTouchDevice() && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={disabled}
            onClick={() => cameraRef.current?.click()}
          >
            {t('upload.takePhoto')}
          </button>
        )}
        {children}
      </div>

      <input
        ref={browseRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={pick(browseRef)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={pick(cameraRef)}
      />
    </div>
  );
}
